import React, { useMemo, useState } from 'react';
import { Image } from 'antd';
import { resolveImageUrl, resolveImageVariant, srcSetDe, modeEffectif } from './imageVariant';
import useDataSaver from '../../hooks/useDataSaver';
import './ndoeImage.css';

// resolveImageUrl vit désormais dans imageVariant.js (pour éviter un cycle
// d'imports) mais reste ré-exportée ici : l'API publique est inchangée pour les
// ~10 fichiers qui l'importent, dont photoComposer.js — lequel doit TOUJOURS
// recevoir l'ORIGINAL haute résolution pour composer les photos WhatsApp.
export { resolveImageUrl };

// Largeurs d'affichage réelles (miroir de ndoeImage.css). `hero` est FLUIDE
// (max-width:100% en mobile, jusqu'à ~430 px) : on prend la borne haute pour ne
// pas le rendre flou — en pratique il servira l'original sur un grand écran.
const SIZE_PX = { sm: 48, md: 64, lg: 72, hero: 430 };

const SIZE_CLASS = {
  sm: 'ndoe-image--sm',
  md: 'ndoe-image--md',
  lg: 'ndoe-image--lg',
  hero: 'ndoe-image--hero',
};

// Placeholder élégant (glyphe « image ») — JAMAIS d'image cassée, d'icône navigateur
// ni de texte alt brut.
const PlaceholderGlyph = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
    <rect x="3" y="4" width="18" height="16" rx="3" fill="none" stroke="currentColor" strokeWidth="1.5" />
    <circle cx="8.5" cy="9.5" r="1.5" fill="currentColor" />
    <path d="M4.5 18l4.5-5 3 3.5L15 13l4.5 5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * NdoeImage — le composant image UNIQUE de l'application.
 * À utiliser PARTOUT à la place de <img> / <Image> pour les photos produit.
 * Gère automatiquement : miniature, ratio carré constant, object-fit cover,
 * placeholder, image absente/erreur, zoom (clic/tap), lazy-loading, a11y.
 *
 * Props :
 *  - src      : chemin/URL de l'image (relatif -> préfixé DOMAIN, absolu conservé)
 *  - alt      : texte alternatif (utilisé pour alt + title + aria-label)
 *  - size     : 'sm'(48) | 'md'(64/60 mobile) | 'lg'(72/64 mobile) | 'hero'(grande) | nombre (px)
 *  - rounded  : coins arrondis (défaut true)
 *  - preview  : clic/tap -> zoom plein écran (défaut true)
 *  - eager    : désactive le lazy-loading (images au-dessus de la ligne de flottaison)
 *  - largeurReelle : largeur d'affichage RÉELLE en px, quand un conteneur écrase
 *      la taille en CSS (ex. .jt-imgwrap force 76 px sur un size="lg")
 *  - sizes    : pour les conteneurs FLUIDES (grilles) — attribut `sizes` HTML
 *      (ex. '(max-width: 767px) 50vw, 200px'). Active srcset : le NAVIGATEUR
 *      choisit alors la vignette d'après la largeur rendue ET la densité réelle,
 *      ce qu'aucun calcul JS ne fait de façon fiable.
 *  - className / style : surcharges éventuelles
 */
export default function NdoeImage({
  src,
  alt = '',
  size = 'md',
  rounded = true,
  preview = true,
  eager = false,
  className = '',
  style,
  largeurReelle,
  sizes,
}) {
  const mode = useDataSaver();
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState(false);
  // Repli explicite : quand le dérivé manque (backfill en cours, génération
  // échouée), on retombe sur l'ORIGINAL — jamais directement sur le placeholder.
  const [replié, setReplié] = useState(false);

  const original = useMemo(() => resolveImageUrl(src), [src]);
  // Largeur d'affichage RÉELLE. `size` ne suffit pas : plusieurs conteneurs de
  // l'app écrasent la taille en CSS (un size="lg" annoncé à 72 px est rendu à
  // 160-200 px dans les catalogues) → un dérivé choisi sur `size` y serait flou.
  // `largeurReelle` permet à ces écrans de dire la vérité.
  const largeur = largeurReelle || SIZE_PX[typeof size === 'string' ? size : ''] || (typeof size === 'number' ? size : 64);
  const url = useMemo(
    () => (replié ? original : resolveImageVariant(src, largeur, mode)),
    [src, largeur, mode, replié, original],
  );
  // Conteneur fluide : on délègue le choix au navigateur (srcset + sizes). En
  // mode Éco on n'expose que les petits paliers, sinon le navigateur choisirait
  // le plus net — ce qui viderait le réglage de son sens.
  const jeu = useMemo(() => {
    if (!sizes || replié) return null;
    const max = modeEffectif(mode) === 'eco' ? 192 : 768;
    return srcSetDe(src, max);
  }, [src, sizes, replié, mode]);

  const sizeClass = typeof size === 'string' ? SIZE_CLASS[size] || SIZE_CLASS.md : '';
  const dim = typeof size === 'number' ? { width: size, height: size } : undefined;
  const wrapClass = `ndoe-image ${sizeClass} ${rounded ? 'is-rounded' : ''} ${className}`.replace(/\s+/g, ' ').trim();
  const label = alt || 'Image indisponible';

  if (!url || failed) {
    return (
      <div
        className={`${wrapClass} ndoe-image--placeholder`}
        style={{ ...dim, ...style }}
        role="img"
        aria-label={label}
        title={label}
      >
        <PlaceholderGlyph />
      </div>
    );
  }

  const openable = preview;

  return (
    <div
      className={wrapClass}
      style={{ ...dim, ...style }}
      tabIndex={openable ? 0 : undefined}
      role={openable ? 'button' : undefined}
      aria-label={openable ? `Agrandir : ${label}` : undefined}
      onKeyDown={
        openable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setOpen(true);
              }
            }
          : undefined
      }
    >
      <Image
        src={url}
        srcSet={jeu || undefined}
        sizes={jeu ? sizes : undefined}
        alt={label}
        title={alt || undefined}
        loading={eager ? 'eager' : 'lazy'}
        // Chaîne de repli : dérivé → original → placeholder.
        onError={() => (replié ? setFailed(true) : setReplié(true))}
        preview={
          openable
            ? {
                visible: open,
                onVisibleChange: setOpen,
                mask: <span className="ndoe-image__mask">Agrandir</span>,
                // Le zoom sert TOUJOURS l'original : sans ce src, antd agrandirait
                // la vignette (un 96 px étiré plein écran).
                src: original,
              }
            : false
        }
      />
    </div>
  );
}
